import { useMemo } from 'react'

import { formatAxisTime, formatInt } from '../../lib/format.ts'
import { SEVERITY_ORDER, severityPresentation } from '../../lib/detections/semantics.ts'
import type { DetectionSummary, Ranked } from '../../lib/detections/semantics.ts'
import type { DetectionAlert, DetectionSeverity } from '../../lib/sse/types.ts'
import { useElementSize } from '../charts/useElementSize.ts'

/**
 * Os quatro níveis lado a lado, com a barra de proporção embaixo. Clicar num
 * nível filtra a fila (no servidor): o panorama é também o filtro.
 */
export function SeverityTiles({
  summary,
  active,
  onPick,
}: {
  summary: DetectionSummary
  active: DetectionSeverity | null
  onPick: (severity: DetectionSeverity | null) => void
}) {
  const total = summary.total
  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-px bg-line-subtle sm:grid-cols-4">
        {SEVERITY_ORDER.map((severity) => {
          const p = severityPresentation(severity)
          const count = summary.bySeverity[severity]
          const selected = active === severity
          return (
            <button
              key={severity}
              type="button"
              aria-pressed={selected}
              onClick={() => onPick(selected ? null : severity)}
              className={`group relative flex flex-col items-start gap-1 bg-surface px-3 py-2.5 text-left transition-colors hover:bg-inset ${
                selected ? p.tint : ''
              }`}
            >
              <span aria-hidden className={`absolute inset-x-0 top-0 h-0.5 ${p.fill} ${count === 0 ? 'opacity-30' : ''}`} />
              <span className={`flex items-center gap-1.5 text-micro font-medium uppercase tracking-label ${p.text}`}>
                <span aria-hidden className={`inline-block size-1.5 ${p.fill}`} />
                {p.label}
              </span>
              <span className={`tnum text-xl leading-none font-medium ${count === 0 ? 'text-fg-muted' : 'text-fg'}`}>
                {formatInt(count)}
              </span>
              <span className="tnum text-micro text-fg-muted">
                {total === 0 ? '—' : `${Math.round((count / total) * 100)}% dos carregados`}
              </span>
              {selected && (
                <span className={`absolute right-2 top-2 text-micro uppercase tracking-label ${p.text}`}>filtro</span>
              )}
            </button>
          )
        })}
      </div>
      <SeverityBar summary={summary} />
    </div>
  )
}

/** Proporção das severidades numa barra só — a "temperatura" da janela. */
export function SeverityBar({ summary }: { summary: DetectionSummary }) {
  if (summary.total === 0) return <div className="h-1.5 bg-line-subtle" />
  return (
    <div className="flex h-1.5 w-full gap-px overflow-hidden" role="img" aria-label="Proporção por severidade">
      {SEVERITY_ORDER.map((severity) => {
        const count = summary.bySeverity[severity]
        if (count === 0) return null
        return (
          <span
            key={severity}
            className={severityPresentation(severity).fill}
            style={{ flexGrow: count }}
            title={`${severityPresentation(severity).label}: ${count}`}
          />
        )
      })}
      {summary.unknownSeverity > 0 && (
        <span className="bg-line-strong" style={{ flexGrow: summary.unknownSeverity }} />
      )}
    </div>
  )
}

/**
 * A cadeia de ataque: uma coluna por tática, na ordem do ATT&CK, inclusive as
 * zeradas. A altura da barra é a contagem; o vazio também informa — "nada de
 * movimento lateral" é uma frase que o analista quer poder dizer.
 */
export function TacticMatrix({ summary }: { summary: DetectionSummary }) {
  const max = Math.max(1, ...summary.tactics.map((t) => t.count))
  return (
    <div className="flex flex-col gap-1.5">
      <ol className="grid grid-flow-col auto-cols-fr items-end gap-px" aria-label="Alertas por tática do MITRE ATT&CK">
        {summary.tactics.map((tactic) => {
          const height = tactic.count === 0 ? 0 : 12 + (tactic.count / max) * 64
          return (
            <li key={tactic.id} className="flex min-w-0 flex-col items-center gap-1" title={`${tactic.id} · ${tactic.label}: ${tactic.count}`}>
              <span className={`tnum font-mono text-micro ${tactic.count === 0 ? 'text-fg-muted' : 'text-fg'}`}>
                {tactic.count === 0 ? '·' : tactic.count}
              </span>
              <span className="flex h-20 w-full items-end bg-inset">
                <span
                  className="w-full bg-accent transition-[height]"
                  style={{ height, opacity: tactic.count === 0 ? 0 : 0.35 + (tactic.count / max) * 0.65 }}
                />
              </span>
              <span
                className={`w-full truncate text-center text-micro ${tactic.count === 0 ? 'text-fg-muted' : 'text-fg-secondary'}`}
              >
                {tactic.short}
              </span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

/**
 * Chegada de alertas no tempo, empilhada por severidade. Eixo = recebimento no
 * servidor (o relógio confiável), não o relógio das máquinas.
 */
export function ActivityChart({ alerts, buckets = 36 }: { alerts: readonly DetectionAlert[]; buckets?: number }) {
  const [ref, size] = useElementSize<HTMLDivElement>()
  const height = 96

  const data = useMemo(() => {
    const times = alerts.map((a) => Date.parse(a.row.received_at)).filter(Number.isFinite)
    if (times.length === 0) return null
    let from = Math.min(...times)
    let to = Math.max(...times)
    // Uma janela de um instante só ainda precisa de largura para desenhar.
    if (to - from < 60_000) {
      const mid = (from + to) / 2
      from = mid - 30_000
      to = mid + 30_000
    }
    const step = (to - from) / buckets
    const columns = Array.from({ length: buckets }, () => ({ critical: 0, high: 0, medium: 0, low: 0, other: 0 }))
    for (const alert of alerts) {
      const at = Date.parse(alert.row.received_at)
      if (!Number.isFinite(at)) continue
      const index = Math.min(buckets - 1, Math.max(0, Math.floor((at - from) / step)))
      const column = columns[index]!
      if (alert.severity === null) column.other += 1
      else column[alert.severity] += 1
    }
    const peak = Math.max(1, ...columns.map((c) => c.critical + c.high + c.medium + c.low + c.other))
    return { from, to, columns, peak }
  }, [alerts, buckets])

  return (
    <div ref={ref} className="w-full">
      {data === null || size.width === 0 ? (
        <div style={{ height: height + 18 }} className="flex items-center justify-center text-micro text-fg-muted">
          {data === null ? 'Sem alertas na janela' : ''}
        </div>
      ) : (
        <svg width={size.width} height={height + 18} role="img" aria-label="Alertas recebidos ao longo do tempo">
          {[0.25, 0.5, 0.75].map((f) => (
            <line
              key={f}
              x1={0}
              x2={size.width}
              y1={height - height * f}
              y2={height - height * f}
              className="stroke-grid"
              strokeDasharray="2 3"
            />
          ))}
          {data.columns.map((column, i) => {
            const w = size.width / data.columns.length
            let y = height
            return (
              <g key={i}>
                {(['low', 'medium', 'high', 'critical'] as const).map((severity) => {
                  const count = column[severity]
                  if (count === 0) return null
                  const h = (count / data.peak) * (height - 6)
                  y -= h
                  return (
                    <rect
                      key={severity}
                      x={i * w + 1}
                      y={y}
                      width={Math.max(1, w - 2)}
                      height={h}
                      className={FILL[severity]}
                    >
                      <title>{`${severityPresentation(severity).label}: ${count}`}</title>
                    </rect>
                  )
                })}
              </g>
            )
          })}
          <line x1={0} x2={size.width} y1={height} y2={height} className="stroke-line" />
          <text x={0} y={height + 13} className="fill-fg-muted font-mono text-micro">
            {formatAxisTime(new Date(data.from))}
          </text>
          <text x={size.width} y={height + 13} textAnchor="end" className="fill-fg-muted font-mono text-micro">
            {formatAxisTime(new Date(data.to))}
          </text>
          <text x={size.width} y={10} textAnchor="end" className="fill-fg-muted font-mono text-micro">
            pico {data.peak}
          </text>
        </svg>
      )}
    </div>
  )
}

const FILL: Record<DetectionSeverity, string> = {
  critical: 'fill-crit',
  high: 'fill-high',
  medium: 'fill-warn',
  low: 'fill-accent',
}

/** Ranking com barra proporcional; a cor é a pior severidade vista na chave. */
export function RankedBars({
  items,
  onPick,
  mono = false,
  empty,
}: {
  items: readonly Ranked[]
  onPick?: (item: Ranked) => void
  mono?: boolean
  empty: string
}) {
  if (items.length === 0) return <p className="py-2 text-micro text-fg-muted">{empty}</p>
  const max = Math.max(...items.map((i) => i.count))
  return (
    <ol className="flex flex-col gap-1">
      {items.map((item) => {
        const p = severityPresentation(item.worst)
        const content = (
          <>
            <span className="flex items-baseline justify-between gap-2">
              <span className={`min-w-0 truncate text-sm text-fg ${mono ? 'font-mono' : ''}`} title={item.label}>
                {item.label}
              </span>
              <span className="tnum shrink-0 font-mono text-micro text-fg-secondary">{item.count}</span>
            </span>
            <span className="block h-1 w-full bg-inset">
              <span className={`block h-full ${p.fill}`} style={{ width: `${(item.count / max) * 100}%` }} />
            </span>
          </>
        )
        return (
          <li key={item.key}>
            {onPick === undefined ? (
              <div className="flex flex-col gap-0.5">{content}</div>
            ) : (
              <button
                type="button"
                onClick={() => onPick(item)}
                className="flex w-full flex-col gap-0.5 text-left hover:[&_span.text-fg]:text-accent"
                title="Filtrar a fila por este item"
              >
                {content}
              </button>
            )}
          </li>
        )
      })}
    </ol>
  )
}
